import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'
import { api } from '@/lib/api'
import { trackEvent } from '@/lib/telemetry'
import { styles } from '@/shell/layout.styles'
import { NAV_ICONS } from '@/shell/nav/navIcons'

export function CoderTemplatesModal({ onClose }: { onClose: () => void }) {
  // Explicit consent: the interest beacon fires on the "Register interest" click,
  // not on open. Feedback is inline (feedback-success/feedback-error) because a
  // toast can't paint over a native <dialog>. Mirrors BetaFeedbackModal/SupportModal.
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  async function register() {
    setState('submitting')
    setErrorMsg('')
    try {
      // Was missing, so coder-templates was the one feature absent from the
      // registered-interest dashboard (deploy/observability/87-interest.yaml),
      // which reads these browser events and not the D1 row.
      trackEvent('register_interest', 'coder-templates')
      await api.post('/api/interest/coder-templates')
      if (mountedRef.current) setState('success')
    } catch (err) {
      if (!mountedRef.current) return
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }

  return (
    <Modal track="coder_templates" title="Coder Templates" kicker="Coming soon" onClose={onClose} maxWidth={720}>
      <p className="guide-para">
        Every{' '} <a href="https://coder.com/docs/admin/templates" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Coder Template</a>{' '}
        is Terraform config you have to publish, pin, and keep in sync yourself.
      </p>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 6 }}>
        How an integration could work
      </div>
      <ul className="guide-list">
        <li><strong>Stop pushing and templates rot</strong> - Terramantle syncs every gated module into Coder automatically.</li>
        <li><strong>Upstream module deprecations and template authors not aware</strong> - Versioned releases and deprecation alerts keep the trail hot.</li>
        <li><strong>Unscanned Terraform ships to every workspace</strong> - Terramantle catches secrets, CVEs, malware and intent first.</li>
        <li><strong>Developers lose their freedom with a locked down catalogue</strong> - Terramantle policy gates enable self-service and guardrails at once at the registry level.</li>
        <li><strong>Unpinned providers break builds at random</strong> - Terramantle serves, pins, and flags the drift.</li>
      </ul>
      <div className="callout-accent">
        <strong>Exploratory.</strong> This integration isn't built yet. Register your interest
        so we can prioritise it.
      </div>
      {state === 'success' ? (
        <div style={{ marginTop: 16 }}>
          <div className="feedback-success">Thanks - we'll prioritise it.</div>
          <div className="modal-actions">
            <a className="btn btn-sm" href="https://github.com/coder/coder" target="_blank" rel="noreferrer">
              View Coder on GitHub ↗
            </a>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <>
          {state === 'error' && (
            <div className="feedback-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
          <div className="modal-actions">
            <a className="btn btn-sm" href="https://github.com/coder/coder" target="_blank" rel="noreferrer">
              View Coder on GitHub ↗
            </a>
            <button className="btn btn-primary btn-sm" onClick={register} disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Registering…' : 'Register interest'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function CoderTemplatesNavButton() {
  const { collapsed } = useSidebar()
  const [hovered, setHovered] = useState(false)
  const [show, setShow] = useState(false)

  const open = () => setShow(true)

  return (
    <>
      {show && <CoderTemplatesModal onClose={() => setShow(false)} />}
      <Tooltip text="Spin up governed cloud dev environments from your modules with Coder workspace templates" placement="right">
        <button
          style={{ ...styles.navItem, ...(collapsed ? styles.navItemCollapsed : {}), ...(hovered ? styles.navItemHover : {}), width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
          onClick={open}
          aria-label={collapsed ? 'Coder Templates' : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {NAV_ICONS.coder}
          {!collapsed && 'Coder Templates'}
        </button>
      </Tooltip>
    </>
  )
}

